import { CharacterHelpers } from "../../../../../entities/characters/Character";
import { SkillHelpers } from "../../../../../entities/library/skills/Skill";
import { RestrictNumber } from "../../../../../entities/Utilities";
import { CharacterSaveSkill, CharacterUpdateSkill, CharacterAdjustSkill, CharacterOverrideSkill } from "../actions/CharacterSkillActions";
import { CharacterState } from "../CharacterStore.State";
import { CharacterReducerHelpers } from "./CharacterReducerHelpers";

const saveSkill = (state: CharacterState, action: CharacterSaveSkill): CharacterState => {
    const { characterId, skillId } = action;
    const character = CharacterReducerHelpers.getCharacter(state, characterId);
    if (character == null) {
        return state;
    }

    const skill = character.skills.find(s => s.id == skillId);
    if (skill == null) {
        console.error('Attempt to save nonexistent skill.');
        return state;
    }

    const pendingProperties = { ...character.pendingProperties };
    delete pendingProperties[skill.instanceId];

    const newCharacter = { ...character, pendingProperties };

    return CharacterReducerHelpers.updateCharacter(state, newCharacter);
}

const updateSkill = (state: CharacterState, action: CharacterUpdateSkill): CharacterState => {
    const { characterId, skillId, updateBy } = action;
    const character = CharacterReducerHelpers.getCharacter(state, characterId);
    if (character == null) {
        return state;
    }

    const skillReference = character.skills.find(s => s.id == skillId);
    if (skillReference == null) {
        console.error('Attempt update nonexistent skill.');
        return state;
    }

    const newLevel = skillReference.level + updateBy;
    const newSkill = { ...skillReference, level: RestrictNumber(newLevel, SkillHelpers.MinValue, SkillHelpers.MaxValue) };

    const newCharacter = {
        ...character,
        skills: [...character.skills.filter(s => s.id != newSkill.id), newSkill]
    };

    const healthRatio = character.currentHealth / CharacterHelpers.getMaxHealth(character);
    const newHealth = Math.round(CharacterHelpers.getMaxHealth(newCharacter) * healthRatio);

    newCharacter.currentHealth = newHealth;
    
    return CharacterReducerHelpers.updateCharacter(state, newCharacter);
};

const adjustSkill = (state: CharacterState, action: CharacterAdjustSkill): CharacterState => {
    const { characterId, skillId, adjustment } = action;
    const character = CharacterReducerHelpers.getCharacter(state, characterId);
    if (character == null) {
        return state;
    }

    const skillReference = character.skills.find(s => s.id == skillId);
    if (skillReference == null) {
        console.error('Attempt adjust nonexistent skill.');
        return state;
    }

    const newSkill = { ...skillReference, adjustment };
    const newCharacter = {
        ...character,
        skills: [...character.skills.filter(s => s.id != newSkill.id), newSkill]
    };

    const healthRatio = newCharacter.currentHealth / CharacterHelpers.getMaxHealth(character);
    newCharacter.currentHealth = Math.round(CharacterHelpers.getMaxHealth(newCharacter) * healthRatio);

    return CharacterReducerHelpers.updateCharacter(state, newCharacter);
}

const overrideSkill = (state: CharacterState, action: CharacterOverrideSkill): CharacterState => {
    let { characterId, skillId, override } = action;
    const character = CharacterReducerHelpers.getCharacter(state, characterId);
    if (character == null) {
        return state;
    }

    const skillReference = character.skills.find(s => s.id == skillId);
    if (skillReference == null) {
        console.error('Attempt override nonexistent skill.');
        return state;
    }
    if (override != undefined && override <= 0) {
        override = undefined;
    }

    const newSkill = { ...skillReference, override };
    const newCharacter = {
        ...character,
        skills: [...character.skills.filter(s => s.id != newSkill.id), newSkill]
    };

    const healthRatio = character.currentHealth / CharacterHelpers.getMaxHealth(character);
    newCharacter.currentHealth = Math.round(CharacterHelpers.getMaxHealth(newCharacter) * healthRatio);

    return CharacterReducerHelpers.updateCharacter(state, newCharacter);
}

export const CharacterSkillReducers = {
    saveSkill,
    updateSkill,
    adjustSkill,
    overrideSkill,
}